import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function AdminAssessment() {
  const [sections, setSections] = useState([]);
  const [questionsBySection, setQBS] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/assessment/schema");
    setSections(data);
    const map = {};
    data.forEach((s) => { map[s.id] = s.questions; });
    setQBS(map);
  };

  useEffect(() => { load(); }, []);

  // ---- Section ops ----
  const addSection = async () => {
    const key = window.prompt("Section key (english, no spaces)");
    if (!key) return;
    const name_en = window.prompt("Name (EN)") || key;
    const name_ar = window.prompt("Name (AR)") || name_en;
    setBusy(true);
    try {
      await api.post("/admin/assessment/sections", { key, name_en, name_ar, order: sections.length });
      toast.success("Section added"); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const updateSection = async (s, patch) => {
    try {
      await api.put(`/admin/assessment/sections/${s.id}`, patch);
      toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSection = async (s) => {
    if (!window.confirm(`Delete section "${s.name_en}" and all its questions?`)) return;
    await api.delete(`/admin/assessment/sections/${s.id}`);
    toast.success("Deleted"); load();
  };

  const moveSection = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const ids = sections.map((x) => x.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.post("/admin/assessment/sections/reorder", { ids });
    load();
  };

  // ---- Question ops ----
  const addQuestion = async (sec) => {
    const key = window.prompt("Question key (unique, e.g. b4)");
    if (!key) return;
    const q_en = window.prompt("Question (EN)") || "";
    const q_ar = window.prompt("Question (AR)") || q_en;
    const weight = parseFloat(window.prompt("Weight (default 1.0)", "1.0") || "1.0");
    setBusy(true);
    try {
      await api.post("/admin/assessment/questions", {
        section_id: sec.id, key, q_en, q_ar, weight,
        q_type: "yes_partial_no", order: (questionsBySection[sec.id] || []).length,
      });
      toast.success("Question added"); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const updateQuestion = async (q, patch) => {
    try {
      await api.put(`/admin/assessment/questions/${q.id}`, patch);
      toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteQuestion = async (q) => {
    if (!window.confirm(`Delete question "${q.key}"?`)) return;
    await api.delete(`/admin/assessment/questions/${q.id}`);
    toast.success("Deleted"); load();
  };

  const moveQuestion = async (sid, idx, dir) => {
    const list = [...(questionsBySection[sid] || [])];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const ids = list.map((x) => x.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.post("/admin/assessment/questions/reorder", { ids });
    load();
  };

  return (
    <div data-testid="admin-assessment">
      <span className="eyebrow">Assessment Builder</span>
      <h1 className="mt-2 text-3xl text-navy">Governance Assessment Questionnaire</h1>
      <span className="divider-gold" />
      <p className="text-navy/70 max-w-3xl">
        Add or edit sections, questions, and weights. Weights are used server-side to compute the final governance score
        (yes = 1.0 × weight, partial = 0.5 × weight, no = 0).
      </p>

      <div className="mt-6 flex justify-end">
        <button data-testid="add-section-btn" onClick={addSection} disabled={busy} className="btn-gold">
          <Plus size={16} /> Add section
        </button>
      </div>

      <div className="mt-6 space-y-6">
        {sections.map((s, idx) => (
          <div key={s.id} className={`card-elev p-6 ${s.published === false ? "opacity-60" : ""}`} data-testid={`sec-${s.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex flex-col gap-1">
                <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="text-navy/60 disabled:opacity-30"><ArrowUp size={14} /></button>
                <button onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1} className="text-navy/60 disabled:opacity-30"><ArrowDown size={14} /></button>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className="field-box !py-2" defaultValue={s.name_en} onBlur={(e) => e.target.value !== s.name_en && updateSection(s, { name_en: e.target.value })} data-testid={`sec-name-en-${s.id}`} />
                <input className="field-box !py-2" dir="rtl" defaultValue={s.name_ar} onBlur={(e) => e.target.value !== s.name_ar && updateSection(s, { name_ar: e.target.value })} data-testid={`sec-name-ar-${s.id}`} />
              </div>
              {s.published === false
                ? <Badge className="bg-amber-100 text-amber-800">Draft</Badge>
                : <Badge className="bg-emerald-100 text-emerald-800">Published</Badge>}
              <button
                onClick={() => updateSection(s, { published: !(s.published !== false) })}
                className="text-navy/70 hover:text-gold p-2"
                title={s.published === false ? "Publish section" : "Save as draft"}
                data-testid={`sec-toggle-${s.id}`}
              >
                {s.published === false ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <button onClick={() => deleteSection(s)} className="text-red-600 hover:text-red-800 p-2" data-testid={`del-sec-${s.id}`}><Trash2 size={16} /></button>
            </div>

            <div className="divide-y divide-navy/10">
              {(questionsBySection[s.id] || []).map((q, qi) => (
                <div key={q.id} className="py-3 flex items-start gap-3" data-testid={`q-${q.id}`}>
                  <div className="flex flex-col gap-1 pt-2">
                    <button onClick={() => moveQuestion(s.id, qi, -1)} disabled={qi === 0} className="text-navy/60 disabled:opacity-30"><ArrowUp size={12} /></button>
                    <button onClick={() => moveQuestion(s.id, qi, 1)} disabled={qi === (questionsBySection[s.id] || []).length - 1} className="text-navy/60 disabled:opacity-30"><ArrowDown size={12} /></button>
                  </div>
                  <div className="w-16 text-xs text-navy/60 pt-2">{q.key}</div>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input className="field-box !py-2" defaultValue={q.q_en} onBlur={(e) => e.target.value !== q.q_en && updateQuestion(q, { q_en: e.target.value })} />
                    <input className="field-box !py-2" dir="rtl" defaultValue={q.q_ar} onBlur={(e) => e.target.value !== q.q_ar && updateQuestion(q, { q_ar: e.target.value })} />
                  </div>
                  <div>
                    <input type="number" step="0.1" min="0" className="field-box !py-2 !w-20 text-center" defaultValue={q.weight} onBlur={(e) => { const w = parseFloat(e.target.value); if (!isNaN(w) && w !== q.weight) updateQuestion(q, { weight: w }); }} title="Weight" data-testid={`q-weight-${q.id}`} />
                    <div className="text-[10px] text-navy/50 text-center mt-1">weight</div>
                  </div>
                  <button
                    onClick={() => updateQuestion(q, { active: !(q.active !== false) })}
                    className={`p-2 ${q.active === false ? "text-amber-700" : "text-emerald-700"}`}
                    title={q.active === false ? "Activate question" : "Deactivate question"}
                    data-testid={`q-toggle-${q.id}`}
                  >
                    {q.active === false ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button onClick={() => deleteQuestion(q)} className="text-red-600 hover:text-red-800 p-2" data-testid={`del-q-${q.id}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <button onClick={() => addQuestion(s)} disabled={busy} className="btn-outline-navy !py-1.5 !px-3 text-xs" data-testid={`add-q-${s.id}`}>
                <Plus size={12} /> Add question to this section
              </button>
            </div>
          </div>
        ))}
        {sections.length === 0 && (
          <div className="text-center text-navy/50 py-10">No sections yet — click &ldquo;Add section&rdquo; to start.</div>
        )}
      </div>
    </div>
  );
}
